<template>
    <component :is="tag" :class="classes"><slot /></component>
</template>

<script lang="ts">
export type HeaderSize = "xxl" | "xl" | "l" | "m" | "s" | "xs" | "xxs";
export type HeaderUsage = "nadia" | "system" | "internal-thinking";

interface BaseHeaderProps {
    size?: HeaderSize;
    usage?: HeaderUsage;
}
</script>

<script setup lang="ts">
import { computed } from "vue";

const { size = "m", usage = "system" } = defineProps<BaseHeaderProps>();

const tagMap: Record<HeaderSize, string> = {
    xxl: "h1",
    xl: "h2",
    l: "h3",
    m: "h4",
    s: "h5",
    xs: "h6",
    xxs: "p",
};

const tag = computed(() => tagMap[size]);

interface TextStyle {
    fontSize: string;
    lineHeight: string;
    fontFamily: string;
    fontWeight: string;
}

type UsageMap = Record<HeaderUsage, TextStyle>;

const styleMap: Record<HeaderSize, UsageMap> = {
    xxl: {
        nadia: { fontSize: "text-[40px]", lineHeight: "leading-[44px]", fontFamily: "font-serif", fontWeight: "font-medium" },
        system: { fontSize: "text-[40px]", lineHeight: "leading-[44px]", fontFamily: "font-sans", fontWeight: "font-semibold" },
        "internal-thinking": { fontSize: "text-[40px]", lineHeight: "leading-[44px]", fontFamily: "font-sans", fontWeight: "font-medium" },
    },
    xl: {
        nadia: { fontSize: "text-[30px]", lineHeight: "leading-[34px]", fontFamily: "font-serif", fontWeight: "font-medium" },
        system: { fontSize: "text-[30px]", lineHeight: "leading-[34px]", fontFamily: "font-sans", fontWeight: "font-semibold" },
        "internal-thinking": { fontSize: "text-[30px]", lineHeight: "leading-[34px]", fontFamily: "font-sans", fontWeight: "font-semibold" },
    },
    l: {
        nadia: { fontSize: "text-[26px]", lineHeight: "leading-[32px]", fontFamily: "font-serif", fontWeight: "font-medium" },
        system: { fontSize: "text-[26px]", lineHeight: "leading-[32px]", fontFamily: "font-sans", fontWeight: "font-semibold" },
        "internal-thinking": { fontSize: "text-[26px]", lineHeight: "leading-[32px]", fontFamily: "font-sans", fontWeight: "font-semibold" },
    },
    m: {
        nadia: { fontSize: "text-[24px]", lineHeight: "leading-[28px]", fontFamily: "font-serif", fontWeight: "font-medium" },
        system: { fontSize: "text-[24px]", lineHeight: "leading-[28px]", fontFamily: "font-sans", fontWeight: "font-medium" },
        "internal-thinking": { fontSize: "text-[24px]", lineHeight: "leading-[28px]", fontFamily: "font-sans", fontWeight: "font-medium" },
    },
    s: {
        nadia: { fontSize: "text-[20px]", lineHeight: "leading-[28px]", fontFamily: "font-serif", fontWeight: "font-bold" },
        system: { fontSize: "text-[20px]", lineHeight: "leading-[26px]", fontFamily: "font-sans", fontWeight: "font-semibold" },
        "internal-thinking": { fontSize: "text-[20px]", lineHeight: "leading-[26px]", fontFamily: "font-sans", fontWeight: "font-semibold" },
    },
    xs: {
        nadia: { fontSize: "text-[20px]", lineHeight: "leading-[24px]", fontFamily: "font-serif", fontWeight: "font-medium" },
        system: { fontSize: "text-[20px]", lineHeight: "leading-[26px]", fontFamily: "font-sans", fontWeight: "font-medium" },
        "internal-thinking": { fontSize: "text-[20px]", lineHeight: "leading-[26px]", fontFamily: "font-sans", fontWeight: "font-medium" },
    },
    xxs: {
        nadia: { fontSize: "text-[13px]", lineHeight: "leading-[18px]", fontFamily: "font-serif", fontWeight: "font-normal" },
        system: { fontSize: "text-[13px]", lineHeight: "leading-[18px]", fontFamily: "font-sans", fontWeight: "font-normal" },
        "internal-thinking": { fontSize: "text-[13px]", lineHeight: "leading-[18px]", fontFamily: "font-sans", fontWeight: "font-medium" },
    },
};

const classes = computed(() => {
    const { fontSize, lineHeight, fontFamily, fontWeight } = styleMap[size][usage];
    return [fontSize, lineHeight, fontFamily, fontWeight].join(" ");
});
</script>
